import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import type { PreferencesMail } from '@/types'
import * as personnesService from '@/services/personnes.service'
import { messageErreur } from '@/utils/messageErreur'

/*
 * Préférences de l'utilisateur, en un seul endroit :
 * - de l'appareil, gardées dans le navigateur (localStorage) : thème, taille du texte, volume, voix et vitesse de lecture,
 *   affichage des listes (grille ou liste) ;
 * - du compte, gardées par le back : e-mails reçus.
 * Les clés existantes sont conservées : changer de clé ferait perdre les réglages déjà faits.
 */
const CLES = {
  theme: 'mamarotte:theme', // lue aussi par les pages légales (public/confidentialite.html, mentions-legales.html)
  taillePolice: 'mamarotte:reader:fontSize',
  volume: 'mamarotte:volume',
  voix: 'mamarotte:tts:voix',
  vitesse: 'mamarotte:tts:vitesse',
  affichageListes: 'mamarotte:affichage-listes',
} as const

/** Pièces et productions : en cartes ou en lignes, un choix commun aux deux pages */
export type AffichageListes = 'grid' | 'list'

export const VITESSES = [0.8, 1, 1.2, 1.5]
export const TAILLE_POLICE_MIN = 12
export const TAILLE_POLICE_MAX = 28
const TAILLE_POLICE_PAR_DEFAUT = 16
const VOLUME_PAR_DEFAUT = 0.8

// Stockage indisponible (navigation privée, données bloquées) : valeurs par défaut, réglages valables pour la session
function lire(cle: string): string | null {
  try {
    return localStorage.getItem(cle)
  } catch {
    return null
  }
}

function ecrire(cle: string, valeur: string | null): void {
  try {
    if (valeur === null) localStorage.removeItem(cle)
    else localStorage.setItem(cle, valeur)
  } catch {
    // ignoré : voir lire()
  }
}

function nombreLu(cle: string, valide: (n: number) => boolean, parDefaut: number): number {
  const n = Number.parseFloat(lire(cle) ?? '')
  return Number.isFinite(n) && valide(n) ? n : parDefaut
}

function borneTaillePolice(taille: number): number {
  return Math.min(TAILLE_POLICE_MAX, Math.max(TAILLE_POLICE_MIN, taille))
}

export const usePreferencesStore = defineStore('preferences', () => {
  // ── Appareil ─────────────────────────────────────────
  const themeLu = lire(CLES.theme)
  const sombre = ref(themeLu ? themeLu === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches)
  const taillePolice = ref(nombreLu(CLES.taillePolice, (n) => n >= TAILLE_POLICE_MIN && n <= TAILLE_POLICE_MAX, TAILLE_POLICE_PAR_DEFAUT))
  const volume = ref(nombreLu(CLES.volume, (n) => n >= 0 && n <= 1, VOLUME_PAR_DEFAUT))
  // Coupure du son partagée entre les lecteurs mais pas gardée : un rechargement ne doit pas rendre l'app muette
  const muet = ref(false)
  const voixUri = ref<string | null>(lire(CLES.voix))
  const vitesse = ref(nombreLu(CLES.vitesse, (n) => VITESSES.includes(n), 1))
  const affichageListes = ref<AffichageListes>(lire(CLES.affichageListes) === 'list' ? 'list' : 'grid')

  watch(
    sombre,
    (valeur) => {
      document.documentElement.setAttribute('data-theme', valeur ? 'dark' : 'light')
      ecrire(CLES.theme, valeur ? 'dark' : 'light')
    },
    { immediate: true }
  )
  watch(taillePolice, (valeur) => ecrire(CLES.taillePolice, String(valeur)))
  watch(volume, (valeur) => ecrire(CLES.volume, String(valeur)))
  watch(voixUri, (valeur) => ecrire(CLES.voix, valeur))
  watch(vitesse, (valeur) => ecrire(CLES.vitesse, String(valeur)))
  watch(affichageListes, (valeur) => ecrire(CLES.affichageListes, valeur))

  function agrandirTexte(): void {
    taillePolice.value = borneTaillePolice(taillePolice.value + 2)
  }

  function reduireTexte(): void {
    taillePolice.value = borneTaillePolice(taillePolice.value - 2)
  }

  // ── Compte ───────────────────────────────────────────
  const mails = ref<PreferencesMail | null>(null)
  const erreurMails = ref<string | null>(null)

  // Chargement principal d'une page : l'erreur, gardée ici, est relancée pour useChargement
  async function chargerMails(): Promise<void> {
    erreurMails.value = null
    try {
      mails.value = await personnesService.getPreferencesMail()
    } catch (e) {
      erreurMails.value = messageErreur(e)
      throw e
    }
  }

  /** Enregistré aussitôt ; en cas d'échec, l'interrupteur revient à sa position et le bandeau dit pourquoi. */
  async function changerMail(cle: keyof PreferencesMail, valeur: boolean): Promise<void> {
    if (!mails.value) return
    const avant = mails.value
    mails.value = { ...avant, [cle]: valeur }
    erreurMails.value = null
    try {
      mails.value = await personnesService.updatePreferencesMail({ [cle]: valeur })
    } catch (e) {
      mails.value = avant
      erreurMails.value = messageErreur(e)
    }
  }

  return {
    sombre,
    taillePolice,
    volume,
    muet,
    voixUri,
    vitesse,
    affichageListes,
    agrandirTexte,
    reduireTexte,
    mails,
    erreurMails,
    chargerMails,
    changerMail,
  }
})
